/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { Menu, Clock, Users, Phone, Mail, MapPin, ChevronRight, CheckCircle, BookOpen, GraduationCap, ArrowRight, FileText, ClipboardList, Star, Lock, Settings, Accessibility, CreditCard, BadgeCheck } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./components/ui/sheet";
import { Badge } from "./components/ui/badge";
import { Toaster } from "./components/ui/sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion";
import { toast } from "sonner";
import { AdminLoginPage, AdminDashboardPage } from "./AdminDashboard";
import { AdminAdvancedPage } from "./AdminAdvanced";
import { ApprenantLoginPage, ApprenantDashboardPage } from "./ApprenantPortal";
import ApprenantFormationPage from "./ApprenantFormationPage";
import { reopenCookieBanner } from "./utils/consentManager";
import { CookieBanner } from "./components/CookieBanner";
import { RgpdNotice } from "./components/RgpdNotice";
import { FORMATIONS, getFormationById } from "./data/formations";
import CheckoutPage from "./CheckoutPage";
import { SessionsPage, SignaturePage, DossierPaiementPage } from "./PresentielPages";
import { TypeBadge, DevisForm, SessionRow, useSessions } from "./components/Presentiel";
import { HomePage as HomePageComp } from "./HomePage";
import { PromoCountdown } from "./components/PromoCountdown";
import { EngagementQualite, BandeauFinanceurs, AccessibiliteBlock, IndicateursQualiopi } from "./components/ReassuranceBlocks";
import { DownloadProgrammePdf } from "./components/DownloadProgrammePdf";
import BacProOtmPage from "./BacProOtmPage";
import AgregationPage from "./AgregationPage";
import CertificatPage from "./CertificatPage";
import ChatWidget from "./components/ChatWidget";
import { FormatriceCard, RecevezBlock, TestimonialsPlaceholder } from "./components/MaryseBlocks";
import { NdaLegalBox, NDA_NUMBER, NDA_SENTENCE } from "./components/NdaMention";
import EnquetePage from "./EnquetePage";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===============================
// LOGO COMPONENT WITH REAL LOGO
// ===============================
const LOGO_URL = "https://customer-assets.emergentagent.com/job_mgb-formations/artifacts/ds42szgw_Design%20MGB%20School%20%285%29.png";

const Logo = ({ className = "", size = "normal" }) => (
  <Link to="/" className={`flex items-center ${className}`} data-testid="logo">
    <img 
      src={LOGO_URL} 
      alt="MGB School - Coaching économie et gestion" 
      className={`w-auto object-contain ${size === "small" ? "h-14 md:h-16" : "h-[4.5rem] md:h-[5.5rem]"}`}
      loading="eager"
      decoding="async"
    />
  </Link>
);

// ===============================
// HEADER COMPONENT
// ===============================
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Accueil", path: "/" },
    { name: "Catalogue", path: "/catalogue" },
    { name: "Sessions", path: "/sessions" },
    { name: "Bac Pro OTM", path: "/otm" },
    { name: "Agrégation", path: "/agregation" },
    { name: "À propos", path: "/a-propos" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-md" : "bg-white shadow-sm"
      }`}
      data-testid="header"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Logo />
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[15px] font-medium transition-colors hover:text-[#F07B1D] ${
                  isActive(link.path) 
                    ? "text-[#003087] font-semibold" 
                    : "text-gray-700"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link to="/catalogue">
              <Button 
                className="bg-[#F07B1D] text-white font-bold px-6 py-5 rounded-lg hover:bg-[#D96A0F] hover:-translate-y-0.5 transition-all shadow-sm hover:shadow-md"
                data-testid="cta-reservation"
              >
                S'inscrire
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <SheetDescription className="sr-only">Navigation principale du site MGB School</SheetDescription>
              <div className="flex flex-col gap-6 mt-8">
                <Logo />
                <nav className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-medium py-2 border-b border-gray-100 ${
                        isActive(link.path) 
                          ? "text-[#003087] font-semibold" 
                          : "text-gray-600"
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>
                <Link to="/catalogue" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#F07B1D] text-white font-bold">
                    S'inscrire
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

// ===============================
// FOOTER COMPONENT
// ===============================
const Footer = () => {
  return (
    <footer className="bg-[#003087] text-white" data-testid="footer">
      <div className="container-custom py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <img 
              src={LOGO_URL} 
              alt="MGB School - Coaching économie et gestion" 
              className="h-20 md:h-24 w-auto object-contain bg-white p-2 rounded-lg"
              data-testid="footer-logo"
            />
            <p className="text-gray-300 text-sm leading-relaxed">
              Organisme de formation spécialisé en économie-gestion, droit social, marketing digital et entrepreneuriat. 25 programmes professionnels.
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-300 pt-1">
              <Accessibility className="h-4 w-4 text-[#F07B1D] shrink-0" />
              <span>Accessible aux personnes en situation de handicap</span>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Navigation</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-[#F07B1D] transition-colors">Accueil</Link></li>
              <li><Link to="/catalogue" className="text-gray-300 hover:text-[#F07B1D] transition-colors">Catalogue · 25 formations</Link></li>
              <li><Link to="/sessions" className="text-gray-300 hover:text-[#F07B1D] transition-colors">Calendrier des sessions présentiel</Link></li>
              <li><Link to="/a-propos" className="text-gray-300 hover:text-[#F07B1D] transition-colors">À propos</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-[#F07B1D] transition-colors">Contact</Link></li>
              <li><Link to="/mentions-legales" className="text-gray-300 hover:text-[#F07B1D] transition-colors">Mentions légales</Link></li>
              <li><Link to="/cgv" className="text-gray-300 hover:text-[#F07B1D] transition-colors">CGV</Link></li>
              <li><Link to="/confidentialite" className="text-gray-300 hover:text-[#F07B1D] transition-colors">Politique de confidentialité</Link></li>
              <li>
                <button
                  type="button"
                  onClick={reopenCookieBanner}
                  className="text-gray-300 hover:text-[#F07B1D] transition-colors text-left"
                  data-testid="footer-manage-cookies"
                >
                  Gérer mes cookies
                </button>
              </li>
              <li><Link to="/apprenant/login" className="text-[#F07B1D] font-semibold hover:text-white transition-colors" data-testid="footer-apprenant-link">🎓 Espace apprenant</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Mail className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                <a href="mailto:contact@mgbschool.fr" className="hover:text-[#F07B1D] transition-colors">contact@mgbschool.fr</a>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Phone className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                <a href="tel:+33758783574" className="hover:text-[#F07B1D] transition-colors">07 58 78 35 74</a>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <MapPin className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                <span>25 rue de Ponthieu<br/>75008 Paris</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300 text-sm">
                <Accessibility className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                <span>Référent Handicap : contact@mgbschool.fr</span>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Informations légales</h4>
            <ul className="space-y-1.5 text-sm text-gray-300">
              <li><span className="font-semibold text-white">Forme :</span> SASU</li>
              <li><span className="font-semibold text-white">SIRET :</span> 999 673 635 00010</li>
              <li><span className="font-semibold text-white">NDA :</span> {NDA_NUMBER}</li>
              <li className="pt-2 text-xs text-gray-400">
                {NDA_SENTENCE}
              </li>
              <li className="text-xs text-gray-400">
                Organisme de formation conforme aux articles L6353-1 et suivants du Code du travail. Formatrice certifiée.
              </li>
              <li className="text-xs text-gray-400">
                Exonération de TVA — art. 261-4-4° du CGI
              </li>
            </ul>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">
              <BadgeCheck className="h-4 w-4 text-[#F07B1D]" />
              <span className="text-xs text-white/90 font-medium">Certificat de réalisation inclus</span>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-400">
              © 2026 MGB School. Tous droits réservés.
            </p>
            <Link to="/admin" className="text-xs text-gray-500 hover:text-[#F07B1D] transition-colors flex items-center gap-1">
              <Settings className="h-3 w-3" />
              Admin
            </Link>
          </div>
          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-4 py-2" data-testid="paypal-badge">
            <Lock className="h-4 w-4 text-[#F07B1D]" />
            <span className="text-sm text-white/90 font-medium">Paiement sécurisé PayPal · Carte bancaire</span>
            <img
              src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg"
              alt="PayPal"
              className="h-5 rounded"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

// ===============================
// TRUST BAR — Bandeau de confiance (catalogue)
// ===============================
const TrustBar = () => {
  const items = [
    { emoji: '🎓', label: '25 formations professionnelles' },
    { emoji: '📍', label: 'Paris 8e · Distanciel disponible' },
    { emoji: '📋', label: 'Organisme déclaré · NDA 11757727775' },
    { emoji: '♿', label: 'Accessible PSH' },
    { emoji: '📜', label: 'Certificat de réalisation inclus' },
  ];
  return (
    <section className="bg-white border-y border-gray-100" data-testid="trust-bar">
      <div className="container-custom py-5">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
          {items.map((it) => (
            <div
              key={it.label}
              className="flex items-center gap-2 md:gap-3"
              data-testid={`trust-item-${it.label}`}
            >
              <span className="text-2xl md:text-3xl shrink-0">{it.emoji}</span>
              <span className="text-xs md:text-sm font-semibold text-[#003087] leading-tight">
                {it.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===============================
// BANDEAU CONFIANCE — Home (financeurs & positionnement)
// ===============================
const BandeauConfiance = () => {
  const message =
    "Organisme de formation déclaré (NDA 11757727775) · Prise en charge institutionnelle (OPCO, France Travail, CPF) : démarches en cours";
  // Triple le message pour un défilement fluide
  const segments = [0, 1, 2];
  return (
    <section
      className="bg-white border-b border-gray-100 overflow-hidden"
      data-testid="bandeau-confiance"
      style={{ padding: "14px 0" }}
    >
      {/* Desktop : centré */}
      <div className="container-custom hidden md:flex items-center justify-center px-12">
        <span className="text-[13px] font-semibold text-gray-700 flex items-center gap-2">
          <span className="text-base text-[#F07B1D]">📋</span>
          {message}
        </span>
      </div>
      {/* Mobile : défilement subtil */}
      <div className="md:hidden relative overflow-hidden">
        <div className="flex gap-12 animate-marquee whitespace-nowrap">
          {segments.map((s) => (
            <span
              key={s}
              className="text-[13px] font-semibold text-gray-700 flex items-center gap-2 shrink-0"
            >
              <span className="text-base text-[#F07B1D]">📋</span>
              {message}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===============================
// PAIEMENT EN LIGNE (PayPal / CB)
// ===============================
const handleReservation = (formation, navigate) => {
  const url = formation.type === "presentiel" ? `/formations/${formation.slug}#devis` : `/paiement/${formation.code}`;
  if (navigate) navigate(url);
  else window.location.href = url;
};

const getReserveLabel = (f) => (f?.type === "presentiel" ? "Demander un devis" : "Réserver");

// ===============================
// HOME PAGE
// ===============================
const HomePage = () => <HomePageComp BandeauConfiance={BandeauConfiance} BandeauFinanceurs={BandeauFinanceurs} />;

// ===============================
// CATALOGUE PAGE
// ===============================
const CATALOG_FILTERS = {
  type: [["all", "Tous"], ["selfservice", "⚡ Self-service"], ["presentiel", "🏫 Présentiel / Mixte"]],
  duree: [["all", "Toutes durées"], ["short", "≤ 7 h"], ["mid", "8 – 14 h"], ["long", "> 14 h"]],
  prix: [["all", "Tous prix"], ["low", "< 300 €"], ["mid", "300 – 600 €"], ["high", "> 600 €"]],
};

const CataloguePage = () => {
  const [filters, setFilters] = React.useState({ type: "all", domain: "all", duree: "all", prix: "all" });
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const domains = ["all", ...Array.from(new Set(FORMATIONS.map((f) => f.domain)))];
  const formations = FORMATIONS.filter((f) =>
    (filters.type === "all" || f.type === filters.type) &&
    (filters.domain === "all" || f.domain === filters.domain) &&
    (filters.duree === "all" || (filters.duree === "short" ? f.hours <= 7 : filters.duree === "mid" ? f.hours > 7 && f.hours <= 14 : f.hours > 14)) &&
    (filters.prix === "all" || (filters.prix === "low" ? f.price < 300 : filters.prix === "mid" ? f.price >= 300 && f.price <= 600 : f.price > 600))
  );
  const selectCls = "h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-[#003087] font-medium";

  const getModalityColor = (modality) => {
    const m = (modality || '').toLowerCase();
    if (m.includes("présentiel")) return "bg-blue-50 text-blue-700 border-blue-200";
    if (m.includes("distanciel")) return "bg-green-50 text-green-700 border-green-200";
    if (m.includes("mixte") || m.includes("hybride")) return "bg-purple-50 text-purple-700 border-purple-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  return (
    <div className="pt-20 md:pt-24" data-testid="catalogue-page">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#003087] relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-96 h-96 bg-[#F07B1D]/15 rounded-full blur-3xl"></div>
        <div className="absolute -left-32 -bottom-32 w-96 h-96 bg-[#2196F3]/15 rounded-full blur-3xl"></div>
        <div className="container-custom relative">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-[2px] bg-[#F07B1D]"></div>
              <span className="text-[#F07B1D] font-semibold text-sm uppercase tracking-wider">Catalogue 2026</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight">
              25 formations professionnelles
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl">
              Économie-gestion, droit social, marketing digital, IA et entrepreneuriat. Toutes nos formations sont exonérées de TVA (art. 261-4-4° CGI).
            </p>
          </div>
        </div>
      </section>

      {/* Trust Bar repris */}
      <TrustBar />

      {/* Formations Grid */}
      <section className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 mb-8 shadow-sm" data-testid="catalogue-filters">
            <div className="flex flex-wrap gap-2 mb-3" data-testid="filter-type">
              {CATALOG_FILTERS.type.map(([v, lab]) => (
                <button key={v} type="button" onClick={() => setF("type", v)} className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${filters.type === v ? "bg-[#003087] border-[#003087] text-white" : "border-gray-200 text-gray-600 hover:border-[#003087]"}`} data-testid={`filter-type-${v}`}>{lab}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <select className={selectCls} value={filters.domain} onChange={(e) => setF("domain", e.target.value)} data-testid="filter-domain">{domains.map((d) => <option key={d} value={d}>{d === "all" ? "Tous les thèmes" : d}</option>)}</select>
              <select className={selectCls} value={filters.duree} onChange={(e) => setF("duree", e.target.value)} data-testid="filter-duree">{CATALOG_FILTERS.duree.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}</select>
              <select className={selectCls} value={filters.prix} onChange={(e) => setF("prix", e.target.value)} data-testid="filter-prix">{CATALOG_FILTERS.prix.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}</select>
            </div>
            <p className="text-xs text-gray-500 mt-3" data-testid="filter-count"><strong className="text-[#003087]">{formations.length}</strong> formation{formations.length > 1 ? "s" : ""} · <span className="text-emerald-700 font-semibold">Self-service</span> = paiement et accès immédiats · <span className="text-[#003087] font-semibold">Présentiel / Mixte</span> = sessions planifiées, sur devis (particulier, entreprise, OPCO)</p>
          </div>
          {formations.length === 0 && <p className="text-center text-gray-500 py-10" data-testid="catalogue-empty">Aucune formation ne correspond à ces filtres.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {formations.map((f) => (
              <div
                key={f.id}
                className={`group relative bg-white rounded-2xl border ${f.featured ? "border-[#F07B1D] shadow-lg shadow-[#F07B1D]/10" : "border-gray-200"} hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden`}
                data-testid={`formation-card-${f.id}`}
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
              >
                {/* Image cover */}
                <div className="relative overflow-hidden" style={{ height: '200px' }}>
                  {f.promoEndsAt && <PromoCountdown endsAt={f.promoEndsAt} variant="card" />}
                  <img
                    src={f.image}
                    alt={f.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                  <div className="absolute top-3 left-3 flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                    <span className="text-base" aria-hidden="true">{f.emoji}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#003087]">{f.domain}</span>
                  </div>
                  <div className="absolute bottom-3 left-3"><TypeBadge type={f.type} /></div>
                  {/* Badge top right */}
                  {f.badge && (
                    <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm ${f.featured ? "bg-[#F07B1D] text-white" : "bg-[#2196F3] text-white"}`}>
                      {f.badge}
                    </div>
                  )}
                </div>

                {/* Header */}
                <div className="px-6 pt-5 pb-3">
                  <p className="text-[11px] text-gray-500 font-mono mb-1">{f.code}</p>
                  <h3 className="font-heading text-lg font-bold text-[#003087] leading-snug mb-2">
                    {f.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {f.description}
                  </p>
                </div>

                {/* Points forts */}
                <div className="px-6 py-3 bg-[#F5F5F5]/50">
                  <ul className="space-y-1.5">
                    {f.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-[#F07B1D] flex-shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  {/* Badge slides incluses */}
                  <div className="mt-3">
                    <span
                      className="inline-block"
                      style={{
                        backgroundColor: "#E3F2FD",
                        color: "#003087",
                        border: "1px solid #2196F3",
                        borderRadius: "100px",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "3px 10px",
                      }}
                      data-testid={`slides-badge-${f.id}`}
                    >
                      📄 Supports PDF professionnels inclus
                    </span>
                  </div>
                </div>

                {/* Meta row */}
                <div className="px-6 py-3 flex flex-wrap items-center gap-2 text-xs text-gray-600 border-t border-gray-100">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-[#F07B1D]" />
                    <span className="font-semibold">{f.duration}</span>
                  </span>
                  <span className="text-gray-300">·</span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5 text-[#F07B1D]" />
                    {f.groupSize}
                  </span>
                  <span className="text-gray-300">·</span>
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getModalityColor(f.modality)}`}>
                    {f.modality}
                  </span>
                </div>

                {/* Price + CTA */}
                <div className="px-6 py-4 mt-auto border-t border-gray-100">
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="font-heading text-3xl font-extrabold text-[#003087]">
                      {f.price}€
                    </span>
                    {f.oldPrice && (
                      <span className="text-sm text-gray-400 line-through">{f.oldPrice}€</span>
                    )}
                    <span className="text-[10px] text-gray-500 ml-auto">Net de taxe</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleReservation(f)}
                      className="flex-1 bg-[#F07B1D] text-white font-bold hover:bg-[#D96A0F] hover:-translate-y-0.5 transition-all shadow-sm hover:shadow-md text-xs sm:text-sm"
                      data-testid={`reserve-btn-${f.id}`}
                    >
                      {getReserveLabel(f)}
                    </Button>
                    <Link to={`/formations/${f.slug}`} className="flex-1">
                      <Button
                        variant="outline"
                        className="w-full border-[#003087] text-[#003087] font-semibold hover:bg-[#003087] hover:text-white"
                        data-testid={`details-btn-${f.id}`}
                      >
                        Détails
                      </Button>
                    </Link>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2 text-center flex items-center justify-center gap-1">
                    <Lock className="h-3 w-3" />
                    Paiement sécurisé · Visa · Mastercard · CB
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom info banner */}
          <div className="mt-12 bg-white rounded-2xl border border-gray-200 p-6 md:p-8" data-testid="catalogue-info-banner">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F07B1D]/10 flex items-center justify-center shrink-0">
                  <CreditCard className="h-5 w-5 text-[#F07B1D]" />
                </div>
                <div>
                  <p className="font-bold text-[#003087] text-sm mb-1">Organisme déclaré · NDA {NDA_NUMBER}</p>
                  <p className="text-xs text-gray-600">Financement individuel ou plan de formation entreprise. Éligibilité OPCO, France Travail et CPF : démarches en cours.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2196F3]/10 flex items-center justify-center shrink-0">
                  <Accessibility className="h-5 w-5 text-[#2196F3]" />
                </div>
                <div>
                  <p className="font-bold text-[#003087] text-sm mb-1">Accessible aux PSH</p>
                  <p className="text-xs text-gray-600">Référent Handicap : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#003087]/10 flex items-center justify-center shrink-0">
                  <BadgeCheck className="h-5 w-5 text-[#003087]" />
                </div>
                <div>
                  <p className="font-bold text-[#003087] text-sm mb-1">Certificat de réalisation</p>
                  <p className="text-xs text-gray-600">Remis à l'issue de chaque formation suivie avec succès.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-white">
        <div className="container-custom text-center">
          <p className="text-gray-600 mb-4">
            Vous ne trouvez pas la formation qu'il vous faut ?
          </p>
          <Link to="/contact">
            <Button variant="outline" className="border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white">
              Contactez-nous pour une demande personnalisée
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// Module accordion default value — stable reference to avoid re-renders
const PROGRAMME_DEFAULT_OPEN = ['m0'];

// ===============================
// FORMATION DETAIL PAGE — /formations/:slug
// ===============================
const FormationDetailPage = () => {
  const { slug } = useParams();
  const formation = getFormationById(slug);
  const sessions = useSessions(formation?.type === "presentiel" ? formation.code : null);

  if (!formation) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center" data-testid="formation-not-found">
        <div className="text-center px-6">
          <h1 className="font-heading text-3xl font-bold text-[#003087] mb-4">Formation introuvable</h1>
          <p className="text-gray-600 mb-6">Cette formation n'existe pas ou a été déplacée.</p>
          <Link to="/catalogue">
            <Button className="bg-[#003087] text-white">Retour au catalogue</Button>
          </Link>
        </div>
      </div>
    );
  }

  const resources = [
    { name: 'Livret apprenant', icon: BookOpen },
    { name: 'Fiches exercices', icon: ClipboardList },
    { name: 'Quiz + corrigé', icon: FileText },
    { name: 'Fiches mémo', icon: FileText },
    { name: 'Supports PDF professionnels', icon: FileText },
    { name: 'Certificat de réalisation', icon: BadgeCheck },
  ];

  const faq = [
    {
      q: 'Cette formation est-elle finançable par mon OPCO ?',
      a: "MGB School est un organisme de formation déclaré (NDA 11757727775, préfet de la région Île-de-France). À ce jour, nos formations ne sont pas encore référencées pour une prise en charge institutionnelle : les démarches sont en cours auprès des OPCO, de France Travail et du CPF. L'inscription se fait à titre individuel ou dans le cadre du plan de développement des compétences de votre entreprise (convention de formation fournie). Contactez-nous à contact@mgbschool.fr pour préparer votre dossier.",
    },
    {
      q: 'Quand vais-je recevoir les ressources pédagogiques ?',
      a: "Immédiatement après votre paiement, vous recevez par e-mail votre facture, vos identifiants d'accès à l'espace apprenant et, pour les formations en présentiel, votre convocation. Les supports PDF (modules, documents types, fiches, exercices corrigés) sont disponibles dans votre espace apprenant.",
    },
    {
      q: 'Êtes-vous accessible aux personnes en situation de handicap ?',
      a: "Oui. Nos formations sont accessibles aux personnes en situation de handicap. Référent Handicap : contact@mgbschool.fr — Chaque situation est étudiée individuellement (aménagements, supports adaptés, modalités).",
    },
    {
      q: 'Obtient-on un certificat à l\'issue de la formation ?',
      a: "Oui, un certificat de réalisation nominatif est remis à la fin de chaque session, conformément aux exigences du décret qualité.",
    },
    {
      q: 'Quelles sont les conditions d\'annulation ?',
      a: "Annulation à plus de 14 jours : remboursement intégral. Entre 7 et 14 jours : 50% du montant reste dû. Moins de 7 jours : la totalité est due. Détails dans nos CGV.",
    },
  ];

  const placeholder = false;

  return (
    <div className="pt-20 md:pt-24" data-testid="formation-detail-page">
      {/* Hero */}
      <section className="py-12 md:py-16 bg-[#003087] relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-96 h-96 bg-[#F07B1D]/15 rounded-full blur-3xl"></div>
        <div className="absolute -left-32 -bottom-32 w-96 h-96 bg-[#2196F3]/15 rounded-full blur-3xl"></div>
        <div className="container-custom relative">
          <Link to="/catalogue" className="inline-flex items-center gap-2 text-white/70 hover:text-[#F07B1D] text-sm font-medium mb-6 transition-colors" data-testid="back-to-catalogue">
            <ArrowRight className="h-4 w-4 rotate-180" />
            Retour au catalogue
          </Link>
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl" aria-hidden="true">{formation.emoji}</span>
                <div>
                  <span className="inline-block bg-[#F07B1D] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    {formation.domain}
                  </span>
                  <span className="ml-2 text-white/60 text-xs font-mono">{formation.code}</span>
                </div>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
                {formation.title}
              </h1>
              {formation.subtitle && <p className="text-[#F07B1D] font-semibold text-base md:text-lg mb-3" data-testid="formation-subtitle">{formation.subtitle}</p>}
              <p className="text-white/85 text-lg mb-6 max-w-2xl">
                {formation.description}
              </p>
              {formation.longDescription && <p className="text-white/75 text-base mb-6 max-w-2xl" data-testid="formation-long-description">{formation.longDescription}</p>}
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="flex items-center gap-1.5 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-white">
                  <Clock className="h-4 w-4 text-[#F07B1D]" /> {formation.duration}
                </span>
                <span className="flex items-center gap-1.5 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-white">
                  <Users className="h-4 w-4 text-[#F07B1D]" /> {formation.groupSize}
                </span>
                <span className="flex items-center gap-1.5 bg-white/10 border border-white/15 px-3 py-1.5 rounded-full text-white">
                  <MapPin className="h-4 w-4 text-[#F07B1D]" /> {formation.modality}
                </span>
                {formation.badge && (
                  <span className="flex items-center gap-1.5 bg-[#F07B1D] text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase">
                    {formation.badge}
                  </span>
                )}
              </div>
            </div>
            {/* Hero image */}
            <div className="hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                <img
                  src={formation.image}
                  alt={formation.title}
                  loading="lazy"
                  className="w-full object-cover"
                  style={{ height: '300px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#003087]/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content + Sticky pricing */}
      <section className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-10">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-10">

              {/* Ce que vous apprenez */}
              <Card className="bg-white p-6 md:p-8" data-testid="section-objectifs">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#F07B1D]/10 flex items-center justify-center">
                    <Star className="h-5 w-5 text-[#F07B1D]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#003087]">Ce que vous apprenez</h2>
                </div>
                <ol className="space-y-3">
                  {formation.objectifs.map((obj, idx) => (
                    <li key={obj} className="flex items-start gap-3">
                      <span className="shrink-0 w-7 h-7 rounded-full bg-[#003087] text-white text-sm font-bold flex items-center justify-center">{idx + 1}</span>
                      <span className="text-gray-700 leading-relaxed pt-0.5">{obj}</span>
                    </li>
                  ))}
                </ol>
              </Card>

              {/* Compétences visées */}
              {formation.competences && formation.competences.length > 0 && (
                <Card
                  className="bg-white p-6 md:p-8 border border-gray-200"
                  style={{ borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
                  data-testid="section-competences"
                >
                  <div className="mb-5">
                    <h2 className="font-heading text-2xl font-bold text-[#003087] inline-block pb-2 relative">
                      Compétences visées
                      <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
                    </h2>
                  </div>
                  <div
                    className="italic text-[#1E293B] text-sm md:text-base mb-5 px-4 py-2.5"
                    style={{
                      backgroundColor: "#EFF4FF",
                      borderLeft: "4px solid #F07B1D",
                      borderRadius: "8px",
                    }}
                  >
                    À l'issue de cette formation, vous serez capable de :
                  </div>
                  <ol className="space-y-0">
                    {formation.competences.map((c, idx) => (
                      <li
                        key={c}
                        className={`flex items-start gap-3 py-3 ${idx < formation.competences.length - 1 ? "border-b border-gray-100" : ""}`}
                      >
                        <span
                          className="shrink-0 w-6 h-6 rounded-full bg-[#2196F3] text-white font-bold flex items-center justify-center"
                          style={{ fontSize: "13px", lineHeight: 1 }}
                        >
                          {idx + 1}
                        </span>
                        <span
                          className="text-[#1E293B]"
                          style={{ fontSize: "14px", lineHeight: 1.6 }}
                        >
                          {c}
                        </span>
                      </li>
                    ))}
                  </ol>
                </Card>
              )}

              {/* Programme */}
              <Card className="bg-white p-6 md:p-8" data-testid="section-programme">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#2196F3]/10 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-[#2196F3]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#003087]">Programme</h2>
                </div>
                <Accordion type="multiple" defaultValue={PROGRAMME_DEFAULT_OPEN} className="space-y-2">
                  {formation.programme.map((mod, idx) => (
                    <AccordionItem key={mod.title} value={`m${idx}`} className="border border-gray-200 rounded-lg px-4 data-[state=open]:border-[#F07B1D]/40 data-[state=open]:bg-[#F07B1D]/[0.02]">
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex flex-col items-start text-left">
                          <span className="font-heading font-bold text-[#003087] text-base">{mod.title}</span>
                          <span className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                            <Clock className="h-3 w-3 text-[#F07B1D]" /> {mod.duration}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-2 pb-2 pl-1">
                          {mod.items.map((it) => (
                            <li key={it} className="flex items-start gap-2 text-sm text-gray-700">
                              <ChevronRight className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>

              {/* Aperçu des slides — bloc visuel "valeur perçue" */}
              {formation.slides && formation.slides.length === 4 && (
                <Card className="bg-white p-6 md:p-8" data-testid="section-slides">
                  <div className="mb-2">
                    <h2 className="font-heading text-2xl font-bold text-[#003087] inline-block pb-2 relative">
                      📄 Aperçu des supports de formation
                      <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Supports PDF professionnels téléchargeables (modules, documents types, fiches, exercices corrigés) — inclus dans chaque module.
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {/* Slide 1 — Module 1 */}
                    <div
                      className="rounded-xl p-3 md:p-4 flex flex-col items-center justify-center text-center text-white relative overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                      style={{
                        background: "#003087",
                        minHeight: "90px",
                        height: "120px",
                      }}
                      data-testid="slide-preview-1"
                    >
                      <span className="absolute top-2 left-2 bg-[#F07B1D] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                        Module 1
                      </span>
                      <p className="text-[11px] md:text-sm font-semibold leading-tight mt-3">
                        {formation.slides[0]}
                      </p>
                    </div>

                    {/* Slide 2 — Module 2 */}
                    <div
                      className="rounded-xl p-3 md:p-4 flex flex-col items-center justify-center text-center text-white relative overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                      style={{
                        background: "#003087",
                        minHeight: "90px",
                        height: "120px",
                      }}
                      data-testid="slide-preview-2"
                    >
                      <span className="absolute top-2 left-2 bg-[#F07B1D] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                        Module 2
                      </span>
                      <p className="text-[11px] md:text-sm font-semibold leading-tight mt-3">
                        {formation.slides[1]}
                      </p>
                    </div>

                    {/* Slide 3 — Étude de cas (dégradé) */}
                    <div
                      className="rounded-xl p-3 md:p-4 flex flex-col items-center justify-center text-center text-white relative overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                      style={{
                        background:
                          "linear-gradient(135deg, #003087 0%, #2196F3 100%)",
                        minHeight: "90px",
                        height: "120px",
                      }}
                      data-testid="slide-preview-3"
                    >
                      <span className="absolute top-2 left-2 bg-[#F07B1D] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                        Étude de cas
                      </span>
                      <p className="text-[11px] md:text-sm font-semibold leading-tight mt-3">
                        {formation.slides[2]}
                      </p>
                    </div>

                    {/* Slide 4 — Quiz */}
                    <div
                      className="rounded-xl p-3 md:p-4 flex flex-col items-center justify-center text-center text-white relative overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all"
                      style={{
                        background: "#003087",
                        minHeight: "90px",
                        height: "120px",
                      }}
                      data-testid="slide-preview-4"
                    >
                      <span className="absolute top-2 left-2 bg-[#22C55E] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                        Quiz inclus ✓
                      </span>
                      <p className="text-[11px] md:text-sm font-semibold leading-tight mt-3">
                        {formation.slides[3]}
                      </p>
                    </div>
                  </div>

                  {/* Encadré sous les miniatures */}
                  <div
                    className="mt-6 p-4 text-sm leading-relaxed text-[#003087]"
                    style={{
                      backgroundColor: "#E3F2FD",
                      borderLeft: "4px solid #F07B1D",
                      borderRadius: "8px",
                    }}
                  >
                    📥 Supports PDF professionnels téléchargeables (modules, documents types, fiches, exercices corrigés) disponibles dans votre espace apprenant dès confirmation de votre inscription. <strong>Accès à vie</strong> · <strong>Mises à jour incluses</strong>
                  </div>
                </Card>
              )}

              {/* Ressources incluses */}
              <Card className="bg-white p-6 md:p-8" data-testid="section-ressources">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#003087]/10 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-[#003087]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#003087]">Ressources incluses</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {resources.map((r) => {
                    const Icon = r.icon;
                    return (
                      <div key={r.name} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-[#F5F5F5]/50">
                        <Icon className="h-5 w-5 text-[#003087] shrink-0" />
                        <span className="text-sm font-medium text-gray-700 flex-1">{r.name}</span>
                        <Lock className="h-3.5 w-3.5 text-gray-400" aria-label="Accessible après inscription" />
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Ressources accessibles après inscription, envoyées sous 24h à votre adresse e-mail.
                </p>
              </Card>

              {/* Ce qui est inclus — bloc optionnel */}
              {formation.inclusions && formation.inclusions.length > 0 && (
                <Card className="bg-white p-6 md:p-8" data-testid="section-inclusions">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-[#F07B1D]/10 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-[#F07B1D]" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-[#003087]">Ce qui est inclus</h2>
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {formation.inclusions.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Modalités d'évaluation — bloc optionnel */}
              {formation.modalitesEvaluation && formation.modalitesEvaluation.length > 0 && (
                <Card className="bg-white p-6 md:p-8" data-testid="section-evaluation">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-[#2196F3]/10 flex items-center justify-center">
                      <BadgeCheck className="h-5 w-5 text-[#2196F3]" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-[#003087]">Modalités d'évaluation</h2>
                  </div>
                  <ol className="space-y-3">
                    {formation.modalitesEvaluation.map((m, idx) => (
                      <li key={m} className="flex items-start gap-3">
                        <span className="shrink-0 w-7 h-7 rounded-full bg-[#2196F3] text-white text-sm font-bold flex items-center justify-center">{idx + 1}</span>
                        <span className="text-gray-700 leading-relaxed pt-0.5">{m}</span>
                      </li>
                    ))}
                  </ol>
                </Card>
              )}

              {/* Public & prérequis */}
              <div className="grid md:grid-cols-2 gap-6" data-testid="section-public-prerequis">
                <Card className="bg-white p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-[#F07B1D]" />
                    <h3 className="font-heading font-bold text-[#003087]">Public cible</h3>
                  </div>
                  <ul className="space-y-2">
                    {formation.publicCible.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
                <Card className="bg-white p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <GraduationCap className="h-5 w-5 text-[#F07B1D]" />
                    <h3 className="font-heading font-bold text-[#003087]">Prérequis</h3>
                  </div>
                  <ul className="space-y-2">
                    {formation.prerequis.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-[#F07B1D] mt-0.5 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>

              {/* Accessibilité - bloc standardisé */}
              <AccessibiliteBlock />

              {formation.type === "presentiel" && (
                <Card className="bg-white p-6 md:p-8 border-2 border-[#003087]/20" data-testid="section-presentiel" id="devis">
                  <div className="flex items-center gap-3 mb-2"><TypeBadge type="presentiel" size="lg" /></div>
                  <h2 className="font-heading text-2xl font-bold text-[#003087] mb-2">Sessions et demande de devis</h2>
                  <p className="text-sm text-gray-600 mb-5">Formation organisée en présentiel (Paris 8e) ou en mixte, en petit groupe. Parcours : devis numéroté → signature électronique du devis puis du contrat/convention → paiement (acompte 30 % possible) → convocation → formation → facture, certificat de réalisation et feuille d'émargement.</p>
                  {sessions.length > 0 ? <div className="space-y-2 mb-6" data-testid="fiche-sessions">{sessions.map((s) => <SessionRow key={s.id} s={s} />)}</div> : <p className="text-sm text-gray-500 mb-6 italic" data-testid="fiche-no-session">Prochaines dates en cours de programmation — demandez un devis, nous vous proposerons une session (inter ou intra-entreprise).</p>}
                  <DevisForm formation={formation} sessions={sessions} />
                </Card>
              )}
              {formation.type === "selfservice" && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="section-selfservice"><TypeBadge type="selfservice" /><p className="text-sm text-emerald-900">Formation 100 % en ligne : paiement immédiat (PayPal / CB), accès immédiat à vos supports PDF (modules, documents types, fiches, exercices corrigés, quiz) dans votre espace apprenant, certificat de réalisation à l'issue. Téléchargement des supports à vie.</p></div>
              )}

              {/* FAQ */}
              <Card className="bg-white p-6 md:p-8" data-testid="section-faq">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#F07B1D]/10 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-[#F07B1D]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#003087]">Questions fréquentes</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-2">
                  {faq.map((item, idx) => (
                    <AccordionItem key={item.q} value={`q${idx}`} className="border border-gray-200 rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline text-left py-4 text-[#003087] font-semibold">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 leading-relaxed pb-4">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>

              {/* Ce que vous recevez */}
              <RecevezBlock formation={formation} />

              {/* Encadré formatrice — avant le bouton Acheter */}
              <FormatriceCard />
            </div>

            {/* Sticky pricing card */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-28 space-y-4">
                <Card className="bg-white border-2 border-[#F07B1D] shadow-xl p-6" data-testid="sticky-pricing">
                  {formation.promoEndsAt && <PromoCountdown endsAt={formation.promoEndsAt} variant="sticky" />}
                  <div className="flex items-center justify-between gap-2 mb-2"><div className="text-xs uppercase tracking-wider font-bold text-[#F07B1D]">{formation.domain}</div><TypeBadge type={formation.type} /></div>
                  <h3 className="font-heading text-xl font-bold text-[#003087] mb-4 leading-tight">{formation.title}</h3>

                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="font-heading text-4xl font-extrabold text-[#003087]">{formation.price}€</span>
                    {formation.oldPrice && (
                      <span className="text-base text-gray-400 line-through">{formation.oldPrice}€</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-5">Net de taxe · Financement individuel ou entreprise · NDA {NDA_NUMBER}</p>

                  <Button
                    onClick={() => (formation.type === "presentiel" ? document.getElementById("devis")?.scrollIntoView({ behavior: "smooth" }) : handleReservation(formation))}
                    className="w-full bg-[#F07B1D] text-white font-bold py-6 text-base hover:bg-[#D96A0F] hover:-translate-y-0.5 transition-all shadow-md hover:shadow-lg mb-3"
                    data-testid="sticky-reserve-btn"
                  >
                    {formation.type === "presentiel" ? `Demander un devis — ${formation.price}€` : formation.ctaLabel ? `${formation.ctaLabel} — ${formation.price}€` : `${getReserveLabel(formation)} — ${formation.price}€`}
                  </Button>

                  <Link to={`/contact?formation=${formation.code}`} className="block">
                    <Button
                      variant="outline"
                      className="w-full border-[#003087] text-[#003087] font-semibold hover:bg-[#003087] hover:text-white"
                    >
                      Poser une question
                    </Button>
                  </Link>

                  <div className="mt-5 pt-5 border-t border-gray-200 space-y-2 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5 text-[#F07B1D]" />
                      Paiement sécurisé · Visa · Mastercard · CB
                    </div>
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="h-3.5 w-3.5 text-[#F07B1D]" />
                      Certificat de réalisation inclus
                    </div>
                    <div className="flex items-center gap-2">
                      <Accessibility className="h-3.5 w-3.5 text-[#F07B1D]" />
                      Accessible aux PSH
                    </div>
                    {placeholder && (
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-[11px]">
                        ℹ️ Inscription par formulaire — un conseiller vous recontactera sous 24h.
                      </div>
                    )}
                  </div>

                  {formation.showFinancementBox && (
                    <div
                      className="mt-4 p-3 rounded-lg text-[12px] leading-relaxed"
                      style={{
                        backgroundColor: '#FFF4E6',
                        border: '1px solid #F07B1D',
                        color: '#7A3B00',
                      }}
                      data-testid="sticky-financement-box"
                    >
                      <strong>Organisme de formation déclaré — NDA {NDA_NUMBER}.</strong><br />
                      Financement individuel ou plan de formation entreprise. Les démarches sont en cours pour la prise en charge OPCO, France Travail et CPF.<br />
                      Contact :{' '}
                      <a
                        href="mailto:contact@mgbschool.fr"
                        className="underline font-semibold"
                      >
                        contact@mgbschool.fr
                      </a>
                    </div>
                  )}
                </Card>

                {/* Bouton téléchargement PDF officiel */}
                <DownloadProgrammePdf formation={formation} />
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Engagement qualité — bloc de réassurance */}
      <EngagementQualite />

      <div className="container-custom mb-12"><NdaLegalBox /></div>

      {/* Ils nous font confiance */}
      <TestimonialsPlaceholder />
    </div>
  );
};


// ===============================
// ABOUT PAGE
// ===============================
const AboutQuote = ({ children }) => (
  <p
    className="italic text-base md:text-lg text-[#003087] my-5 px-4 py-2"
    style={{ borderLeft: "4px solid #F07B1D" }}
  >
    {children}
  </p>
);

const AboutBlocTitle = ({ children }) => (
  <h3 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-3 inline-block pb-1 relative">
    {children}
    <span className="absolute left-0 bottom-0 w-10 h-[3px] bg-[#F07B1D] rounded-full"></span>
  </h3>
);

const AboutPage = () => {
  const credentials = [
    "Formation Ingénieure d'affaires commerce international (École des Cadres)",
    "DU commerce extérieur · droit des affaires · gestion des entreprises",
    "20 ans chef d'entreprise transport",
    "Agrément douane reconnu en Europe",
    "Transport routier · aérien · multimodal",
    "Enseignante Bac Pro OTM",
    "Candidate Agrégation 2025",
    "Formatrice professionnelle certifiée",
    "Fondatrice COLIMEDIA + MGB School",
    "Praticienne IA au quotidien",
  ];

  return (
    <div className="pt-20 md:pt-24 bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="py-12 md:py-16 bg-[#003087]">
        <div className="container-custom max-w-4xl flex flex-col md:flex-row items-center gap-6 md:gap-8 text-center md:text-left">
          <img
            src="/maryse-galita.jpg"
            alt="Maryse GALITA"
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              objectFit: "cover",
              border: "4px solid #F07B1D",
              flexShrink: 0,
              boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            }}
          />
          <div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-2 leading-tight">
              Maryse GALITA
            </h1>
            <p className="text-base md:text-lg text-white/90">
              Fondatrice de MGB School
            </p>
          </div>
        </div>
      </section>

      {/* Citation principale */}
      <section className="py-10 bg-[#F5F7FA]">
        <div className="container-custom max-w-3xl">
          <div
            className="bg-white p-6 md:p-8 rounded-xl shadow-sm"
            style={{ borderLeft: "6px solid #F07B1D" }}
          >
            <p className="text-xl md:text-2xl italic font-semibold text-[#003087] leading-relaxed">
              « Je n'enseigne pas ce que j'ai lu. J'enseigne ce que j'ai vécu. »
            </p>
          </div>
        </div>
      </section>

      {/* Blocs */}
      <section className="py-12 md:py-16">
        <div className="container-custom max-w-3xl space-y-10">
          {/* Bloc 0 — Formation initiale */}
          <div>
            <AboutBlocTitle>Formation initiale</AboutBlocTitle>
            <div className="text-gray-700 leading-relaxed space-y-3 mt-3">
              <p>
                Avant de créer Jet Courses, je me suis formée aux deux piliers de ce que j'enseigne aujourd'hui :
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong>Formation Ingénieure d'affaires en commerce international</strong> — École des Cadres de Courbevoie (aujourd'hui EDC Paris Business School)
                </li>
                <li>
                  <strong>Diplôme universitaire de commerce extérieur, droit des affaires et gestion des entreprises</strong>
                </li>
              </ul>
              <p>
                Ces deux cursus couvrent les fondamentaux que vous retrouvez dans toutes les formations MGB School : Incoterms, douane, droit des contrats, droit social, comptabilité, gestion d'entreprise.
              </p>
            </div>
          </div>

          {/* Bloc 1 */}
          <div>
            <AboutBlocTitle>Chef d'entreprise transport pendant 20 ans</AboutBlocTitle>
            <div className="text-gray-700 leading-relaxed space-y-3 mt-3">
              <p>
                En 1999, j'ai créé <strong>Jet Courses</strong> en Guadeloupe — une entreprise de transport routier de fret et de transport aérien avec une antenne à l'aéroport d'Orly et un parc de véhicules de location.
              </p>
              <p>
                J'ai obtenu un <strong>agrément en douane reconnu au niveau européen</strong> — ce qui signifie que mes déclarations douanières étaient validées et connues de l'ensemble des autorités douanières européennes.
              </p>
              <p>J'ai personnellement géré cette entreprise multimodale pendant 20 ans :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Transport routier de fret</li>
                <li>Transport aérien (Orly)</li>
                <li>Déclarations douanières agréées Europe</li>
                <li>Gestion d'un parc de véhicules de location</li>
                <li>Management d'équipe</li>
                <li>Comptabilité et gestion financière</li>
                <li>Droit social appliqué</li>
              </ul>
              <AboutQuote>
                Lettres de voiture, Bill of Lading, DAU, Incoterms, gestion des stocks, facturation — je les ai pratiqués avant de les enseigner.
              </AboutQuote>
            </div>
          </div>

          {/* Bloc 2 */}
          <div>
            <AboutBlocTitle>Enseignante en lycée professionnel</AboutBlocTitle>
            <div className="text-gray-700 leading-relaxed space-y-3 mt-3">
              <p>
                À l'issue de cette expérience entrepreneuriale, j'ai rejoint l'Éducation nationale comme professeure d'économie-gestion en lycée professionnel, spécialité Transport et Logistique.
              </p>
              <p>
                J'ai accompagné <strong>des centaines d'élèves</strong> vers le Bac Pro OTM — en transmettant non pas des théories mais une expérience terrain de 20 ans.
              </p>
            </div>
          </div>

          {/* Bloc 3 */}
          <div>
            <AboutBlocTitle>Candidate à l'agrégation</AboutBlocTitle>
            <div className="text-gray-700 leading-relaxed space-y-3 mt-3">
              <p>
                En 2025, l'année où j'ai terminé mon contrat à l'Éducation nationale, j'ai préparé et passé l'agrégation d'économie-gestion.
              </p>
              <p>
                Cette expérience m'a permis de maîtriser en profondeur la <strong>méthodologie dissertation</strong>, la mise en tension dialectique et les exigences précises des jurys de concours — compétences que je transmets dans le Pack Dissertation Management Stratégique.
              </p>
            </div>
          </div>

          {/* Bloc 4 */}
          <div>
            <AboutBlocTitle>Formatrice certifiée et praticienne de l'IA</AboutBlocTitle>
            <div className="text-gray-700 leading-relaxed space-y-3 mt-3">
              <p>
                Je suis <strong>certifiée formatrice professionnelle</strong>. J'utilise l'intelligence artificielle au quotidien dans ma pratique pédagogique, ce qui me permet de créer des contenus actualisés, précis et immédiatement applicables dans le monde professionnel.
              </p>
            </div>
          </div>

          {/* Bloc 5 */}
          <div>
            <AboutBlocTitle>Pourquoi MGB School</AboutBlocTitle>
            <div className="text-gray-700 leading-relaxed space-y-3 mt-3">
              <p>
                J'ai créé MGB School parce que les formations que j'aurais aimé avoir — claires, pratiques, accessibles sans se déplacer, fondées sur l'expérience réelle — n'existaient pas sous cette forme.
              </p>
              <AboutQuote>
                Chaque formation de MGB School est le fruit de 20 ans d'expérience terrain, d'un parcours académique complet et d'une pratique quotidienne de l'enseignement et de l'IA.
              </AboutQuote>
            </div>
          </div>
        </div>
      </section>

      {/* Tableau légitimité */}
      <section className="py-12 bg-[#F5F7FA]">
        <div className="container-custom max-w-4xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#003087] text-center mb-8">
            Pourquoi vous pouvez me faire confiance
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {credentials.map((c) => (
              <div
                key={c}
                className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm"
                data-testid="credential-row"
              >
                <span className="text-xl text-[#F07B1D] leading-none flex-shrink-0">✅</span>
                <span className="text-sm text-gray-800 font-medium">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Indicateurs Qualiopi (version enrichie) */}
      <IndicateursQualiopi />

      {/* Engagement qualité */}
      <EngagementQualite />

      {/* CTA */}
      <section className="py-12 bg-white">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl font-bold text-[#003087] mb-4">
            Prêt à vous former ?
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/catalogue">
              <Button className="bg-[#003087] text-white font-bold">
                Découvrir nos formations
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-[#003087] text-[#003087]">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// ===============================
// CONTACT PAGE
// ===============================
const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedFormation = searchParams.get("formation") || "";

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    formation: preselectedFormation,
    situation: "",
    financement: "",
    handicap: "",
    message: "",
    consent_rgpd: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent_rgpd) {
      toast.error("Merci de cocher la case de consentement RGPD.");
      return;
    }
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        consent_rgpd: true,
        consent_timestamp: new Date().toISOString(),
      };
      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      toast.success("Message envoyé avec succès !");
      setFormData({
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        formation: "",
        situation: "",
        financement: "",
        handicap: "",
        message: "",
        consent_rgpd: false,
      });
    } catch {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 md:pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-16 bg-[#003087] relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-96 h-96 bg-[#F07B1D]/15 rounded-full blur-3xl"></div>
        <div className="container-custom text-center relative">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-4">
            Contactez-nous
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Une question ? Un projet de formation ? Nous sommes là pour vous accompagner.
          </p>
        </div>
      </section>

      <section className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#003087] mb-6">
                Nos coordonnées
              </h2>
              <div className="space-y-4">
                <Card className="bg-white">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#003087]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-[#003087]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#003087] mb-0.5">Email</h3>
                        <a href="mailto:contact@mgbschool.fr" className="text-gray-600 hover:text-[#F07B1D]">contact@mgbschool.fr</a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#003087]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-[#003087]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#003087] mb-0.5">Téléphone</h3>
                        <a href="tel:+33758783574" className="text-gray-600 hover:text-[#F07B1D]">07 58 78 35 74</a>
                        <p className="text-xs text-gray-500 mt-1">Du lundi au vendredi, 9h-18h</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#003087]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-[#003087]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#003087] mb-0.5">Adresse</h3>
                        <p className="text-gray-600">25 rue de Ponthieu<br />75008 Paris</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#2196F3]/5 border-[#2196F3]/30">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <Accessibility className="h-5 w-5 text-[#2196F3] mt-0.5 shrink-0" />
                      <div>
                        <h3 className="font-bold text-[#003087] mb-1 text-sm">Référent Handicap</h3>
                        <p className="text-xs text-gray-700 leading-relaxed">
                          Nos formations sont accessibles aux PSH. Chaque situation est étudiée individuellement : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#F07B1D]/15 rounded-full flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="h-5 w-5 text-[#F07B1D]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#003087] mb-0.5">Rendez-vous</h3>
                        <p className="text-gray-600 text-sm mb-2">Échangeons sur votre projet</p>
                        <a href="tel:+33758783574">
                          <Button variant="outline" size="sm" className="border-[#F07B1D] text-[#F07B1D] hover:bg-[#F07B1D] hover:text-white">
                            Appeler maintenant
                          </Button>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#003087] mb-6">
                Envoyez-nous un message
              </h2>

              {submitted ? (
                <Card className="bg-white" data-testid="contact-success">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-green-600 mb-2">
                      Demande envoyée !
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Nous reviendrons vers vous dans les meilleurs délais à l'adresse <strong>contact@mgbschool.fr</strong>.
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline">
                      Envoyer une autre demande
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white">
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="nom" className="text-sm font-semibold text-[#003087]">Nom *</Label>
                          <Input id="nom" name="nom" value={formData.nom} onChange={handleChange} required className="mt-1" data-testid="input-nom" />
                        </div>
                        <div>
                          <Label htmlFor="prenom" className="text-sm font-semibold text-[#003087]">Prénom *</Label>
                          <Input id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} required className="mt-1" data-testid="input-prenom" />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-sm font-semibold text-[#003087]">E-mail *</Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-1" data-testid="input-email" />
                      </div>

                      <div>
                        <Label htmlFor="telephone" className="text-sm font-semibold text-[#003087]">Téléphone</Label>
                        <Input id="telephone" name="telephone" type="tel" value={formData.telephone} onChange={handleChange} className="mt-1" data-testid="input-telephone" />
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-[#003087]">Formation souhaitée</Label>
                        <Select value={formData.formation} onValueChange={(v) => handleSelectChange("formation", v)}>
                          <SelectTrigger className="mt-1" data-testid="select-formation">
                            <SelectValue placeholder="Sélectionnez une formation" />
                          </SelectTrigger>
                          <SelectContent>
                            {FORMATIONS.map((f) => (
                              <SelectItem key={f.id} value={f.code}>
                                {f.emoji} {f.code} — {f.title}
                              </SelectItem>
                            ))}
                            <SelectItem value="autre">Autre / Je ne sais pas encore</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-[#003087]">Situation actuelle</Label>
                        <Select value={formData.situation} onValueChange={(v) => handleSelectChange("situation", v)}>
                          <SelectTrigger className="mt-1" data-testid="select-situation">
                            <SelectValue placeholder="Votre situation" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="chef_entreprise">Chef d'entreprise</SelectItem>
                            <SelectItem value="independant">Indépendant</SelectItem>
                            <SelectItem value="demandeur_emploi">Demandeur d'emploi</SelectItem>
                            <SelectItem value="public_insertion">Public en insertion</SelectItem>
                            <SelectItem value="porteur_projet">Porteur de projet</SelectItem>
                            <SelectItem value="salarie_reconversion">Salarié en reconversion</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-[#003087]">Mode de financement</Label>
                        <Select value={formData.financement} onValueChange={(v) => handleSelectChange("financement", v)}>
                          <SelectTrigger className="mt-1" data-testid="select-financement">
                            <SelectValue placeholder="Votre mode de financement" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="personnel">Paiement personnel</SelectItem>
                            <SelectItem value="opco">OPCO</SelectItem>
                            <SelectItem value="cpf">CPF</SelectItem>
                            <SelectItem value="france_travail">France Travail</SelectItem>
                            <SelectItem value="ne_sait_pas">Je ne sais pas</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="handicap" className="text-sm font-semibold text-[#003087] flex items-center gap-1.5">
                          <Accessibility className="h-3.5 w-3.5 text-[#2196F3]" />
                          Besoin d'adaptation handicap (optionnel)
                        </Label>
                        <Textarea
                          id="handicap"
                          name="handicap"
                          value={formData.handicap}
                          onChange={handleChange}
                          rows={2}
                          className="mt-1"
                          placeholder="Décrivez vos besoins d'adaptation éventuels (locaux, supports, modalités...)"
                          data-testid="input-handicap"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-sm font-semibold text-[#003087]">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={4}
                          className="mt-1"
                          placeholder="Décrivez votre projet de formation..."
                          data-testid="input-message"
                        />
                      </div>

                      {/* Consentement RGPD explicite (preuve horodatée) */}
                      <div
                        className="flex items-start gap-3 p-3 rounded-lg"
                        style={{ backgroundColor: "#E3F2FD", border: "1px solid #2196F3" }}
                      >
                        <input
                          type="checkbox"
                          id="consent_rgpd"
                          name="consent_rgpd"
                          checked={formData.consent_rgpd}
                          onChange={handleChange}
                          required
                          className="mt-1 h-4 w-4 shrink-0 accent-[#F07B1D] cursor-pointer"
                          data-testid="checkbox-consent-rgpd"
                        />
                        <label htmlFor="consent_rgpd" className="text-xs text-[#003087] leading-relaxed cursor-pointer">
                          <strong>J'accepte que MGB School traite ma demande</strong> et me recontacte à propos de mon projet
                          de formation. Mes données sont conservées 1 an après le dernier contact et ne sont jamais
                          revendues à des tiers (RGPD — Règlement UE 2016/679).{" "}
                          <Link
                            to="/confidentialite"
                            className="underline text-[#F07B1D] font-semibold"
                            target="_blank"
                            rel="noopener"
                          >
                            En savoir plus
                          </Link>
                        </label>
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting || !formData.consent_rgpd}
                        className="w-full bg-[#F07B1D] text-white font-bold py-6 text-base hover:bg-[#D96A0F] hover:-translate-y-0.5 transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="submit-contact"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Envoi en cours...
                          </>
                        ) : (
                          "Envoyer ma demande"
                        )}
                      </Button>

                      <RgpdNotice className="text-center" testid="rgpd-notice-contact" />
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ===============================
// PAIEMENT CONFIRME PAGE
// ===============================
const PaiementConfirmePage = () => {
  const [searchParams] = useSearchParams();
  const purchaseFired = useRef(false);

  // Meta Pixel — Purchase event (uniquement sur cette page de succès, une seule fois)
  useEffect(() => {
    if (purchaseFired.current) return;
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;

    const rawAmount = searchParams.get("amount") || searchParams.get("value") || "0";
    const orderId =
      searchParams.get("order_id") ||
      searchParams.get("session_id") ||
      searchParams.get("transaction_id") ||
      searchParams.get("commande") ||
      "";
    const value = parseFloat(String(rawAmount).replace(",", ".")) || 0;

    const payload = { value, currency: "EUR" };
    if (orderId) payload.transaction_id = orderId;

    try {
      window.fbq("track", "Purchase", payload);
      purchaseFired.current = true;
       
      console.log("[Meta Pixel] Purchase fired:", payload);
    } catch (e) {
       
      console.warn("[Meta Pixel] Purchase track failed", e);
    }
  }, [searchParams]);

  return (
    <div className="pt-20 md:pt-24 min-h-screen bg-[#F5F5F5]" data-testid="paiement-confirme-page">
      <section className="section-padding">
        <div className="container-custom max-w-2xl">
          <Card className="bg-white p-8 md:p-12 text-center border-2 border-green-200 shadow-xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-[#003087] mb-3">
              ✅ Paiement confirmé !
            </h1>
            <p className="text-lg text-gray-700 mb-2 leading-relaxed">
              Vos identifiants d'accès à l'espace apprenant et votre convocation arrivent <strong>sous 24h ouvrées</strong> à votre adresse e-mail.
            </p>
            {searchParams.get("facture") && (
              <p className="text-sm text-gray-500 mb-6" data-testid="paiement-facture-numero">
                Facture n° <strong className="text-[#003087]">{searchParams.get("facture")}</strong>{searchParams.get("formation") ? ` · Formation ${searchParams.get("formation")}` : ""}
              </p>
            )}

            <div
              className="text-left text-sm text-gray-700 leading-relaxed p-4 mb-6 rounded-lg"
              style={{ backgroundColor: "#E3F2FD", borderLeft: "4px solid #2196F3" }}
              data-testid="paiement-facture-note"
            >
              📄 Votre facture acquittée (avec NDA et exonération de TVA) est générée automatiquement et envoyée à votre adresse e-mail, ainsi que votre reçu PayPal. Si vous ne la recevez pas sous 24h, contactez-nous : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline font-medium">contact@mgbschool.fr</a>
            </div>

            <div className="bg-[#F5F5F5] rounded-lg p-5 mb-6 text-left">
              <h3 className="font-heading font-bold text-[#003087] mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#F07B1D]" />
                Pour toute question
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#F07B1D] shrink-0" />
                  <a href="mailto:contact@mgbschool.fr" className="text-[#003087] hover:text-[#F07B1D] font-medium">contact@mgbschool.fr</a>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-[#F07B1D] shrink-0" />
                  <a href="tel:+33758783574" className="text-[#003087] hover:text-[#F07B1D] font-medium">07 58 78 35 74</a>
                </p>
              </div>
            </div>

            <Link to="/catalogue">
              <Button className="bg-[#F07B1D] text-white font-bold px-8 py-6 text-base hover:bg-[#D96A0F] hover:-translate-y-0.5 transition-all shadow-md" data-testid="back-to-catalogue-btn">
                Retour au catalogue
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>

            <p className="text-xs text-gray-500 mt-6">
              Un certificat de réalisation vous sera remis à l'issue de la formation.
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};

// ===============================
// LEGAL PAGES
// ===============================
const LegalPage = ({ title, children }) => (
  <div className="pt-20 md:pt-24">
    <section className="py-12 bg-[#003087]">
      <div className="container-custom">
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-white">
          {title}
        </h1>
      </div>
    </section>
    <section className="section-padding">
      <div className="container-custom max-w-4xl">
        <div className="prose prose-lg max-w-none">
          {children}
        </div>
      </div>
    </section>
  </div>
);

const MentionsLegales = () => (
  <LegalPage title="Mentions légales">
    <Card className="bg-white p-8">
      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4">Éditeur du site</h2>
      <p className="text-gray-600 mb-4">
        <strong>MGB School</strong><br />
        Société par actions simplifiée unipersonnelle (SASU)<br />
        Organisme de formation professionnelle<br />
        25 rue de Ponthieu, 75008 Paris<br />
        SIRET : 999 673 635 00010<br />
        Numéro de déclaration d'activité (NDA) : <strong>{NDA_NUMBER}</strong>
      </p>
      <p className="text-sm text-gray-700 p-4 rounded-lg border-l-4 border-[#F07B1D] mb-4" style={{ backgroundColor: "#F5F7FA" }} data-testid="mentions-nda-declaration">
        {NDA_SENTENCE}
      </p>

      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4 mt-8">Directrice de la publication</h2>
      <p className="text-gray-600 mb-4">
        Maryse GALITA
      </p>

      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4 mt-8">Contact</h2>
      <p className="text-gray-600 mb-4">
        Email : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a><br />
        Téléphone : 07 58 78 35 74
      </p>

      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4 mt-8">Hébergement</h2>
      <p className="text-gray-600 mb-4">
        Site hébergé par <strong>IONOS</strong><br />
        IONOS SARL — 7 place de la Gare — BP 70109 — 57201 Sarreguemines Cedex<br />
        <a href="https://www.ionos.fr" className="text-[#F07B1D] underline" target="_blank" rel="noopener noreferrer">www.ionos.fr</a>
      </p>

      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4 mt-8">Fiscalité</h2>
      <p className="text-gray-600 mb-4">
        MGB School est exonéré de TVA au titre de la formation professionnelle continue (article 261-4-4° du Code Général des Impôts).
      </p>

      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4 mt-8">Accessibilité</h2>
      <p className="text-gray-600 mb-4">
        Nos formations sont accessibles aux personnes en situation de handicap. Référent Handicap : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a>. Chaque situation est étudiée individuellement.
      </p>

      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4 mt-8">Propriété intellectuelle</h2>
      <p className="text-gray-600">
        L'ensemble du contenu de ce site (textes, images, logo) est protégé par le droit d'auteur. Toute reproduction est interdite sans autorisation préalable.
      </p>
      <h2 className="font-heading text-xl font-bold text-[#003087] mb-4 mt-8">Médiation de la consommation</h2>
      <p className="text-gray-600 mb-2">
        Conformément aux articles L611-1 et suivants du Code de la consommation, en cas de litige non résolu à l'amiable, le consommateur peut saisir gratuitement le médiateur de la consommation dont relève MGB School :
      </p>
      <p className="text-gray-800 font-semibold" data-testid="mediateur-mentions">
        CM2C — 49 rue de Ponthieu, 75008 Paris — <a href="https://www.cm2c.net" target="_blank" rel="noopener noreferrer" className="text-[#F07B1D] underline">www.cm2c.net</a>
      </p>
    </Card>
  </LegalPage>
);

const CGV = () => (
  <div className="pt-20 md:pt-24" data-testid="cgv-page">
    {/* Hero */}
    <section className="py-12 md:py-16 bg-[#003087] relative overflow-hidden">
      <div className="absolute -right-24 -top-24 w-80 h-80 bg-[#F07B1D]/15 rounded-full blur-3xl"></div>
      <div className="absolute -left-24 -bottom-24 w-80 h-80 bg-[#2196F3]/15 rounded-full blur-3xl"></div>
      <div className="container-custom relative">
        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-3">
          Conditions Générales de Vente
        </h1>
        <p className="text-white/80 text-base md:text-lg mb-5">
          MGB School — Organisme de formation professionnelle
        </p>
        <div className="flex flex-wrap gap-3">
          <span className="inline-flex items-center bg-[#F07B1D] text-white text-xs font-bold px-3 py-1.5 rounded-full">
            Dernière mise à jour : Mai 2026
          </span>
          <span className="inline-flex items-center bg-[#2196F3] text-white text-xs font-bold px-3 py-1.5 rounded-full">
            Conformes au Code du travail
          </span>
        </div>
      </div>
    </section>

    <section className="section-padding bg-[#F5F5F5]">
      <div className="container-custom max-w-4xl space-y-8">
        {/* Section 1 — Identification */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-1">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-4 inline-block pb-1 relative">
            1 · Identification de l'organisme
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>
          <ul className="text-gray-700 leading-relaxed space-y-1.5">
            <li><strong>MGB School</strong> — SASU</li>
            <li>25 rue de Ponthieu, 75008 Paris</li>
            <li>SIRET : 999 673 635 00010</li>
            <li>NDA : Déclaration d'activité enregistrée auprès de la DREETS Île-de-France — organisme de formation professionnelle déclaré, dispensant des formations conformes aux articles L6353-1 et suivants du Code du travail. Formatrice certifiée.</li>
            <li>Téléphone : <a href="tel:+33758783574" className="text-[#F07B1D] underline">07 58 78 35 74</a></li>
            <li>E-mail : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a></li>
            <li>TVA : Non applicable — article 261-4-4° du CGI (organismes de formation exonérés de TVA)</li>
          </ul>
        </Card>

        {/* Section 2 — Deux types de contrats */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-2">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-6 inline-block pb-1 relative">
            2 · Deux types de contrats
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div
              className="p-5 rounded-lg"
              style={{ backgroundColor: "#E3F2FD", borderLeft: "4px solid #2196F3" }}
            >
              <h3 className="font-heading font-bold text-[#003087] mb-2">
                👤 Contrat de formation pour les particuliers
              </h3>
              <p className="text-xs text-gray-600 mb-3 italic">
                (article L6353-3 du Code du travail)
              </p>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                Le contrat de formation professionnelle est établi entre MGB School et le stagiaire particulier. Il mentionne obligatoirement :
              </p>
              <ul className="text-sm text-gray-700 space-y-1 mb-3 list-disc pl-5">
                <li>L'intitulé, le niveau et la durée de la formation</li>
                <li>Les objectifs et le programme</li>
                <li>Les modalités d'organisation et de suivi</li>
                <li>Les modalités d'évaluation</li>
                <li>Les qualifications des formateurs</li>
                <li>Le prix et les modalités de paiement</li>
                <li>Les conditions d'annulation</li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed">
                Ce contrat vous est envoyé par e-mail dans les 24h suivant votre inscription. Vous disposez de <strong>10 jours calendaires</strong> pour le signer ou vous rétracter <em>(art. L6353-5 du Code du travail)</em>.
              </p>
            </div>

            <div
              className="p-5 rounded-lg"
              style={{ backgroundColor: "#FFF3E8", borderLeft: "4px solid #F07B1D" }}
            >
              <h3 className="font-heading font-bold text-[#003087] mb-2">
                🏢 Convention de formation pour les entreprises et OPCO
              </h3>
              <p className="text-xs text-gray-600 mb-3 italic">
                (article L6353-1 du Code du travail)
              </p>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                La convention de formation est établie entre MGB School et l'entreprise ou l'organisme financeur. Elle précise obligatoirement :
              </p>
              <ul className="text-sm text-gray-700 space-y-1 mb-3 list-disc pl-5">
                <li>L'intitulé et la nature de l'action</li>
                <li>Le niveau de formation visé</li>
                <li>Les effectifs concernés</li>
                <li>Les dates et la durée de la formation</li>
                <li>Le lieu de réalisation</li>
                <li>Le prix et les conditions de règlement</li>
                <li>Les modalités de suivi et d'évaluation</li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed">
                Sur demande à <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a>, nous établissons votre convention sous <strong>48h ouvrées</strong> <em>(art. L6353-1 et L6353-2 du Code du travail)</em>.
              </p>
            </div>
          </div>
        </Card>

        {/* Section 3 — Rétractation */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-3">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-4 inline-block pb-1 relative">
            3 · Délai de rétractation
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>
          <div
            className="p-5 rounded-lg mb-4"
            style={{ backgroundColor: "#F5F7FA", borderLeft: "4px solid #F07B1D" }}
          >
            <p className="font-bold text-[#003087] mb-2 flex items-center gap-2">
              ⚖️ Délai de rétractation
            </p>
            <p className="text-xs text-gray-600 mb-3 italic">
              (Article L6353-5 du Code du travail)
            </p>
            <p className="text-sm text-gray-700 mb-3 leading-relaxed">
              Conformément à l'article L6353-5 du Code du travail, tout stagiaire particulier bénéficie d'un délai de rétractation de <strong>10 jours calendaires</strong> à compter de la date de signature du contrat individuel de formation.
            </p>
            <p className="text-sm text-gray-700 mb-2 leading-relaxed font-semibold">
              Pendant ce délai de 10 jours :
            </p>
            <ul className="text-sm text-gray-700 space-y-1 mb-2 list-disc pl-5">
              <li>Aucune somme ne peut être exigée du stagiaire, à quelque titre que ce soit</li>
              <li>Le stagiaire peut se rétracter sans motif ni pénalité</li>
              <li>La rétractation s'effectue par e-mail à <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a> ou par courrier recommandé avec accusé de réception</li>
            </ul>
          </div>

          <div className="p-5 rounded-lg bg-orange-50 border border-orange-200">
            <p className="font-bold text-[#003087] mb-2">
              ⚠️ Cas particulier — contenus numériques à accès immédiat
            </p>
            <p className="text-sm text-gray-700 mb-3 leading-relaxed">
              Conformément à l'article L221-28 12° du Code de la consommation et à la directive européenne 2011/83/UE (article 16 m), le droit de rétractation ne s'applique pas aux contenus numériques fournis sur un support immatériel dont l'exécution a commencé avec l'accord préalable exprès du consommateur, qui a reconnu qu'il perdra son droit de rétractation.
            </p>
            <p className="text-sm text-gray-700 mb-3 leading-relaxed">
              En cochant la case d'acceptation lors du paiement, le stagiaire reconnaît expressément avoir demandé l'accès immédiat aux contenus et renonce à son droit de rétractation pour les formations à accès immédiat.
            </p>
            <p className="text-sm text-gray-700 italic">
              Cette renonciation expresse est conforme à la jurisprudence de la CJUE et à la position de la DGCCRF.
            </p>
          </div>
        </Card>

        {/* Section 4 — Paiement */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-4">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-2 inline-block pb-1 relative">
            4 · Modalités de paiement
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>
          <p className="text-xs text-gray-600 italic mb-5">(Article L6353-6 du Code du travail)</p>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="p-5 rounded-lg border border-gray-200 bg-[#F5F7FA]">
              <h3 className="font-heading font-bold text-[#003087] mb-3">
                Particuliers — paiement immédiat en ligne
              </h3>
              <ul className="text-sm text-gray-700 space-y-1.5 list-disc pl-5">
                <li>Paiement sécurisé via PayPal ou carte bancaire (SSL/TLS)</li>
                <li>Paiement unique au moment de l'inscription</li>
                <li>Facture acquittée émise automatiquement par MGB School et envoyée par e-mail</li>
                <li>TVA non applicable — art. 261-4-4° CGI</li>
                <li>Accès aux contenus immédiat après paiement (sous réserve de renonciation expresse au droit de rétractation)</li>
              </ul>
            </div>

            <div className="p-5 rounded-lg border border-gray-200 bg-[#F5F7FA]">
              <h3 className="font-heading font-bold text-[#003087] mb-3">
                Entreprises — prise en charge OPCO
              </h3>
              <ul className="text-sm text-gray-700 space-y-1.5 list-disc pl-5">
                <li>Convention de formation établie avant le début de la formation</li>
                <li>Acompte : 30 % maximum à l'entrée en formation, après expiration du délai de rétractation de 10 jours</li>
                <li>Solde : échelonné au fur et à mesure du déroulement de la formation <em>(art. L6353-6 du Code du travail)</em></li>
                <li>Facturation conforme aux exigences de votre OPCO</li>
                <li>Règlement par virement bancaire ou chèque libellé à l'ordre de MGB School</li>
              </ul>
            </div>
          </div>

          <div className="mt-5 p-4 rounded-lg" style={{ backgroundColor: "#FFF3E8", borderLeft: "4px solid #F07B1D" }}>
            <p className="text-sm text-gray-700">
              <strong>Prise en charge France Travail</strong> — sur devis. Contactez-nous : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a> · <a href="tel:+33758783574" className="text-[#F07B1D] underline">07 58 78 35 74</a>
            </p>
          </div>
        </Card>

        {/* Section 5 — Attestation */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-5">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-2 inline-block pb-1 relative">
            5 · Attestation et évaluation
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>
          <p className="text-xs text-gray-600 italic mb-4">(Article L6353-5 du Code du travail)</p>

          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            MGB School délivre à chaque stagiaire ayant suivi l'intégralité de la formation et validé l'évaluation finale une <strong>attestation individuelle de fin de formation</strong> mentionnant :
          </p>
          <ul className="text-sm text-gray-700 space-y-1 mb-4 list-disc pl-5">
            <li>Les objectifs de la formation</li>
            <li>La nature et la durée de l'action</li>
            <li>Les résultats de l'évaluation des acquis</li>
          </ul>
          <p className="text-sm text-gray-700 mb-4">
            <strong>Délai de délivrance :</strong> 7 jours ouvrés après la fin de la formation.
          </p>

          <p className="text-sm font-semibold text-[#003087] mb-2">Modalités d'évaluation par formation :</p>
          <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
            <li>Questionnaire de positionnement initial</li>
            <li>Évaluations formatives à chaque module</li>
            <li>Évaluation finale sommative</li>
            <li>Questionnaire de satisfaction J+1 et J+30</li>
          </ul>
        </Card>

        {/* Section 6 — RGPD */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-6">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-2 inline-block pb-1 relative">
            6 · Protection des données — RGPD
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>
          <p className="text-xs text-gray-600 italic mb-4">(Règlement UE 2016/679 — RGPD)</p>

          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p><strong>Responsable du traitement :</strong> MGB School · <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a></p>
            <p><strong>Données collectées :</strong> nom, prénom, e-mail, données de paiement (traitées par PayPal, certifié PCI-DSS niveau 1 — MGB School ne stocke aucune donnée bancaire).</p>
            <p><strong>Finalités :</strong> gestion administrative et pédagogique de la formation, émission des factures et attestations, suivi de la satisfaction.</p>
            <p><strong>Durée de conservation :</strong> 5 ans à compter de la fin de la relation contractuelle (obligation comptable — art. L123-22 du Code de commerce).</p>
            <p><strong>Droits :</strong> accès, rectification, effacement, portabilité, opposition — exercer vos droits à <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a></p>
            <p>Les données ne sont jamais vendues ni cédées à des tiers à des fins commerciales. PayPal (Europe) S.à r.l. et Cie, S.C.A. (Luxembourg) est établi dans l'UE et soumis au RGPD.</p>
          </div>
        </Card>

        {/* Section 7 — PI */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-7">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-2 inline-block pb-1 relative">
            7 · Propriété intellectuelle
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>
          <p className="text-xs text-gray-600 italic mb-4">
            (Articles L111-1 et suivants du Code de la propriété intellectuelle)
          </p>

          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            L'ensemble des contenus MGB School — supports PDF, fiches auteurs, dissertations corrigées, quiz, vidéos, documents PDF, méthodes pédagogiques — sont la propriété intellectuelle exclusive de MGB School.
          </p>
          <p className="text-sm font-semibold text-[#003087] mb-1">La licence accordée au stagiaire est :</p>
          <ul className="text-sm text-gray-700 space-y-1 mb-4 list-disc pl-5">
            <li>Personnelle et non cessible</li>
            <li>Limitée à l'usage pédagogique individuel</li>
            <li>Non commerciale</li>
          </ul>
          <p className="text-sm text-gray-700 leading-relaxed">
            Toute reproduction, diffusion, revente ou utilisation à des fins commerciales est strictement interdite et constitue une <strong>contrefaçon</strong> passible de sanctions pénales (jusqu'à <strong>3 ans d'emprisonnement et 300 000 € d'amende</strong> — art. L335-2 du Code de la propriété intellectuelle).
          </p>
        </Card>

        {/* Section 8 — Litiges */}
        <Card className="bg-white p-6 md:p-8" data-testid="cgv-section-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-4 inline-block pb-1 relative">
            8 · Règlement des litiges
            <span className="absolute left-0 bottom-0 w-12 h-[3px] bg-[#F07B1D] rounded-full"></span>
          </h2>

          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            En cas de litige, le stagiaire peut recourir gratuitement à la médiation :
          </p>
          <p className="text-sm font-semibold text-[#003087] mb-1">Médiateur de la consommation :</p>
          <p className="text-sm text-gray-700 mb-3 leading-relaxed">
            Conformément aux articles L611-1 et suivants et L612-1 du Code de la consommation, après une réclamation écrite restée sans réponse satisfaisante dans un délai de deux mois, le stagiaire consommateur peut saisir gratuitement le médiateur de la consommation dont relève MGB School :
          </p>
          <div className="rounded-lg border border-[#003087]/20 bg-[#003087]/5 p-4 mb-4 text-sm text-gray-800" data-testid="mediateur-cgv">
            <p className="font-bold text-[#003087]">CM2C — Centre de la Médiation de la Consommation de Conciliateurs de Justice</p>
            <p>49 rue de Ponthieu<br />75008 Paris</p>
            <p>Site : <a href="https://www.cm2c.net" target="_blank" rel="noopener noreferrer" className="text-[#F07B1D] underline">www.cm2c.net</a> — saisine en ligne ou par courrier.</p>
          </div>
          <p className="text-sm text-gray-700 mb-4 leading-relaxed">
            Le stagiaire peut également utiliser la plateforme européenne de règlement en ligne des litiges : <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-[#F07B1D] underline">ec.europa.eu/consumers/odr</a>.
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            En cas d'échec de la médiation, le tribunal compétent est celui du ressort du siège social de MGB School : <strong>Tribunal de commerce de Paris</strong>.
          </p>
        </Card>

        {/* Pied — encadré navy */}
        <div
          className="rounded-2xl p-6 md:p-8 text-center text-white"
          style={{ background: "#003087" }}
          data-testid="cgv-footer-box"
        >
          <p className="text-sm md:text-base leading-relaxed mb-1">
            <strong>MGB School</strong> · SASU · 25 rue de Ponthieu, 75008 Paris
          </p>
          <p className="text-sm md:text-base leading-relaxed mb-1">
            SIRET 999 673 635 00010 · NDA {NDA_NUMBER}
          </p>
          <p className="text-xs text-white/70 leading-relaxed mb-2 max-w-2xl mx-auto">{NDA_SENTENCE}</p>
          <p className="text-sm md:text-base leading-relaxed mb-1">
            <a href="mailto:contact@mgbschool.fr" className="underline hover:text-[#F07B1D]">contact@mgbschool.fr</a> · <a href="tel:+33758783574" className="underline hover:text-[#F07B1D]">07 58 78 35 74</a>
          </p>
          <p className="text-xs text-white/70 mb-5">Dernière mise à jour : Mai 2026</p>
          <a href="mailto:contact@mgbschool.fr">
            <Button
              className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold px-6 py-5 text-sm"
              data-testid="cgv-contact-btn"
            >
              Nous contacter →
            </Button>
          </a>
        </div>
      </div>
    </section>
  </div>
);

const Confidentialite = () => (
  <LegalPage title="Politique de confidentialité et protection des données personnelles">
    {/* Badge conformité */}
    <div
      className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-sm"
      style={{ backgroundColor: "#E3F2FD", color: "#003087", border: "1px solid #2196F3" }}
      data-testid="rgpd-badge"
    >
      <Lock className="h-4 w-4 text-[#F07B1D]" />
      Conforme RGPD — Règlement UE 2016/679 — Dernière mise à jour : Juillet 2026
    </div>

    <Card className="bg-white p-6 md:p-8">
      <p className="text-gray-700 leading-relaxed mb-6">
        MGB School attache une importance particulière à la protection de vos données personnelles. Cette politique
        décrit de manière transparente comment nous collectons, utilisons, conservons et protégeons vos données,
        conformément au <strong>Règlement (UE) 2016/679</strong> (RGPD) et à la loi Informatique et Libertés du 6 janvier 1978 modifiée.
      </p>

      {/* 1 — Responsable de traitement */}
      <ConfSection num="1" title="Identité du responsable de traitement">
        <div className="grid md:grid-cols-2 gap-3 text-gray-700 text-sm">
          <div className="p-4 rounded-lg" style={{ backgroundColor: "#F5F7FA" }}>
            <p className="font-semibold text-[#003087] mb-2">MGB School — SASU</p>
            <p>25 rue de Ponthieu, 75008 Paris</p>
            <p>SIRET : <strong>999 673 635 00010</strong></p>
            <p>NDA : <strong>{NDA_NUMBER}</strong> (préfet de la région Île-de-France)</p>
            <p>E-mail : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a></p>
            <p>Téléphone : <a href="tel:+33758783574" className="text-[#F07B1D] underline">07 58 78 35 74</a></p>
            <p>Site web : <a href="https://mgbschool.com" className="text-[#F07B1D] underline">mgbschool.com</a></p>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: "#FFF4E6", borderLeft: "4px solid #F07B1D" }}>
            <p className="font-semibold text-[#003087] mb-2">Représentant légal et responsable du traitement</p>
            <p><strong>Maryse GALITA</strong></p>
            <p className="text-xs text-gray-600 mt-1">Présidente et Formatrice professionnelle certifiée</p>
          </div>
        </div>
      </ConfSection>

      {/* 2 — Données collectées et finalités */}
      <ConfSection num="2" title="Données collectées et finalités">
        <ConfSubtitle>2.1 · Données collectées lors de l'inscription à une formation</ConfSubtitle>
        <ConfTable
          head={["Donnée", "Finalité", "Base légale"]}
          rows={[
            ["Nom et prénom", "Gestion administrative de la formation", "Exécution du contrat (art. 6.1.b RGPD)"],
            ["Adresse e-mail", "Envoi des accès, supports et attestation", "Exécution du contrat"],
            ["Numéro de téléphone", "Contact pédagogique et suivi", "Exécution du contrat"],
            ["Adresse postale", "Émission de la convention et des factures", "Obligation légale (art. 6.1.c RGPD)"],
            ["Numéro SIRET (si professionnel)", "Convention de formation, facturation", "Obligation légale"],
            ["Données de paiement", "Traitement de la transaction sécurisée", "Exécution du contrat"],
            ["Niveau de compétences initial", "Questionnaire de positionnement pédagogique", "Intérêt légitime pédagogique (art. 6.1.f RGPD)"],
            ["Résultats aux évaluations", "Délivrance de l'attestation de fin de formation", "Exécution du contrat"],
          ]}
        />

        <ConfSubtitle className="mt-6">2.2 · Formulaire de contact</ConfSubtitle>
        <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
          <li>Nom, prénom, adresse e-mail, message</li>
          <li>Finalité : réponse à votre demande</li>
          <li>Base légale : <strong>consentement</strong> (art. 6.1.a RGPD)</li>
        </ul>

        <ConfSubtitle className="mt-6">2.3 · Chatbot IA</ConfSubtitle>
        <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
          <li>Questions posées et échanges avec l'assistant IA</li>
          <li>Adresse e-mail si fournie volontairement</li>
          <li>Finalité : assistance pédagogique et commerciale</li>
          <li>Base légale : <strong>consentement</strong> (art. 6.1.a RGPD)</li>
        </ul>

        <ConfSubtitle className="mt-6">2.4 · Données de navigation (cookies)</ConfSubtitle>
        <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
          <li>Adresse IP, type de navigateur, pages visitées, durée de visite</li>
          <li>Finalité : analyse d'audience et amélioration du site</li>
          <li>Base légale : <strong>consentement</strong> (art. 6.1.a RGPD) pour les cookies non essentiels</li>
        </ul>
      </ConfSection>

      {/* 3 — Sous-traitants */}
      <ConfSection num="3" title="Sous-traitants et destinataires des données">
        <p className="text-gray-700 text-sm mb-3">
          MGB School fait appel aux sous-traitants suivants, tous engagés contractuellement à respecter le RGPD :
        </p>
        <ConfTable
          head={["Sous-traitant", "Rôle", "Localisation", "Garanties"]}
          rows={[
            ["PayPal (Europe) S.à r.l. et Cie, S.C.A.", "Traitement des paiements sécurisés (PayPal et carte bancaire)", "Luxembourg (UE)", "Certifié PCI-DSS niveau 1 — établissement de paiement agréé (CSSF)"],
            ["Anthropic", "Moteur du chatbot IA", "États-Unis", "Politique de confidentialité Anthropic — DPA disponible"],
            ["Meta Platforms", "Pixel publicitaire (ID 338077314135026) — chargé uniquement après votre consentement « Publicité »", "États-Unis", "Clauses contractuelles types UE — Data Privacy Framework"],
            ["PostHog Inc.", "Mesure d'audience (et enregistrement de session si consentement dédié) — chargé uniquement après votre consentement", "États-Unis", "Clauses contractuelles types UE — DPA PostHog"],
            ["Hébergeur du site", "Hébergement et infrastructure", "UE", "Accord de traitement des données"],
          ]}
        />
        <div className="mt-4 p-4 rounded-lg text-sm text-[#7A3B00]" style={{ backgroundColor: "#FFF4E6", borderLeft: "4px solid #F07B1D" }}>
          <strong>Aucune donnée personnelle n'est vendue à des tiers.</strong> Aucune donnée n'est transmise à des fins
          commerciales sans votre consentement explicite.
        </div>
      </ConfSection>

      {/* 4 — Durée de conservation */}
      <ConfSection num="4" title="Durée de conservation des données">
        <ConfTable
          head={["Catégorie de données", "Durée de conservation", "Fondement"]}
          rows={[
            ["Données de formation (convention, attestation, feuilles d'émargement)", "5 ans après la fin de la formation", "Art. L123-22 Code de commerce"],
            ["Données de facturation", "10 ans", "Art. L123-22 Code de commerce — Obligation comptable"],
            ["Questionnaire de positionnement", "5 ans", "Exigences Qualiopi — Indicateur 8"],
            ["Questionnaires de satisfaction (J+1 et J+30)", "3 ans", "Intérêt légitime — Amélioration continue"],
            ["Données de contact (formulaire, chatbot)", "1 an après le dernier contact", "Intérêt légitime"],
            ["Cookies analytiques", "13 mois maximum", "Recommandation CNIL"],
            ["Données de navigation", "13 mois maximum", "Recommandation CNIL"],
          ]}
        />
      </ConfSection>

      {/* 5 — Droits */}
      <ConfSection num="5" title="Vos droits">
        <p className="text-gray-700 text-sm mb-4">
          Conformément au RGPD (Règlement UE 2016/679) et à la loi Informatique et Libertés du 6 janvier 1978 modifiée,
          vous disposez des droits suivants :
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            ["Droit d'accès (art. 15)", "Obtenir la confirmation qu'un traitement vous concerne et en recevoir une copie."],
            ["Droit de rectification (art. 16)", "Faire corriger des données inexactes ou incomplètes."],
            ["Droit à l'effacement (art. 17)", "Demander la suppression de vos données (sous réserve des obligations légales de conservation)."],
            ["Droit à la limitation (art. 18)", "Suspendre temporairement le traitement de vos données."],
            ["Droit à la portabilité (art. 20)", "Recevoir vos données dans un format structuré et lisible par machine."],
            ["Droit d'opposition (art. 21)", "Vous opposer au traitement fondé sur l'intérêt légitime, notamment à la prospection commerciale."],
            ["Droit de retirer votre consentement", "À tout moment, sans remise en cause de la licéité du traitement antérieur."],
          ].map(([title, body]) => (
            <div
              key={title}
              className="p-3 bg-white rounded-lg"
              style={{ border: "1px solid #F07B1D", borderLeftWidth: "4px" }}
            >
              <p className="font-semibold text-[#003087] text-sm mb-1">{title}</p>
              <p className="text-xs text-gray-700 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: "#E3F2FD" }}>
          <p className="font-semibold text-[#003087] mb-2">Comment exercer vos droits</p>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>Par e-mail : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a></li>
            <li>Par courrier : MGB School — 25 rue de Ponthieu, 75008 Paris</li>
            <li>Délai de réponse : <strong>1 mois maximum</strong> (art. 12 RGPD)</li>
          </ul>
        </div>

        <div className="mt-4 p-4 rounded-lg text-sm text-gray-700" style={{ backgroundColor: "#F5F7FA", borderLeft: "4px solid #003087" }}>
          <p className="font-semibold text-[#003087] mb-1">Réclamation auprès de la CNIL</p>
          <p>
            En cas de réponse insatisfaisante, vous pouvez saisir la Commission Nationale de l'Informatique et des Libertés :
          </p>
          <p className="mt-1">CNIL — 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07</p>
          <p><a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#F07B1D] underline">www.cnil.fr</a> — Téléphone : 01 53 73 22 22</p>
        </div>
      </ConfSection>

      {/* 6 — Cookies */}
      <ConfSection num="6" title="Cookies et traceurs">
        <div className="mb-4 p-4 rounded-lg text-sm text-gray-700" style={{ backgroundColor: "#F5F7FA", borderLeft: "4px solid #F07B1D" }} data-testid="conf-cookie-principle">
          <p className="font-semibold text-[#003087] mb-1">Principe : aucun traceur non essentiel avant votre accord</p>
          <p>
            À votre arrivée sur le site, seuls les cookies strictement nécessaires sont utilisés. Les outils de mesure d'audience (PostHog), de publicité (Meta Pixel) et d'enregistrement de session ne sont <strong>ni chargés ni autorisés à déposer un cookie</strong> tant que vous n'avez pas cliqué sur « Tout accepter » ou activé la catégorie concernée via « Personnaliser ». Refuser est aussi simple qu'accepter (« Tout refuser »). <strong>Base légale :</strong> consentement (art. 82 de la loi Informatique et Libertés, art. 6.1.a RGPD), retirable à tout moment via « Gérer mes cookies ». Votre choix est conservé 6 mois, puis le bandeau vous est présenté à nouveau. Chaque choix est horodaté et journalisé (identifiant de consentement anonyme, sans adresse IP en clair) afin de pouvoir en apporter la preuve.
          </p>
        </div>

        <ConfSubtitle>6.1 · Cookies strictement nécessaires (toujours actifs — pas de consentement requis)</ConfSubtitle>
        <ConfTable
          head={["Cookie / stockage", "Finalité", "Durée"]}
          rows={[
            ["mgbApprenantToken (stockage local)", "Authentification de l'espace apprenant", "Jusqu'à déconnexion"],
            ["Session d'administration", "Accès sécurisé au tableau de bord MGB School", "Session"],
            ["PayPal (cookies du module de paiement)", "Exécution du paiement — nécessaire à la fourniture du service", "Session / selon PayPal"],
            ["mgb_cookie_consent (stockage local)", "Mémorisation de votre choix cookies", "6 mois"],
          ]}
        />

        <ConfSubtitle className="mt-6">6.2 · Statistiques / mesure d'audience (consentement requis) — PostHog</ConfSubtitle>
        <ConfTable
          head={["Cookie", "Finalité", "Durée"]}
          rows={[
            ["ph_phc_…_posthog", "Mesure d'audience : pages vues, parcours agrégés, sans enregistrement de session", "12 mois"],
            ["Stockage local PostHog", "Identifiant anonyme de visite", "12 mois"],
          ]}
        />

        <ConfSubtitle className="mt-6">6.3 · Publicité / marketing (consentement requis) — Meta Pixel</ConfSubtitle>
        <ConfTable
          head={["Cookie", "Finalité", "Durée"]}
          rows={[
            ["_fbp", "Mesure des conversions publicitaires Facebook / Instagram", "90 jours"],
            ["_fbc", "Attribution des clics publicitaires Meta", "90 jours"],
          ]}
        />

        <ConfSubtitle className="mt-6">6.3 bis · Enregistrement de session / heatmaps (consentement dédié) — PostHog Session Replay</ConfSubtitle>
        <p className="text-sm text-gray-700 mb-2">
          Désactivé par défaut, y compris lorsque vous cliquez sur « Tout accepter ». Activable uniquement via « Personnaliser » : rejoue anonymement votre navigation (sans saisie de mots de passe ni données de paiement) pour détecter les blocages d'ergonomie. Même durée et même cookie que la mesure d'audience.
        </p>

        <ConfSubtitle className="mt-6">6.4 · Comment gérer ou retirer votre consentement</ConfSubtitle>
        <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
          <li>Via le bandeau de consentement affiché à votre première visite (« Tout accepter », « Tout refuser », « Personnaliser »)</li>
          <li>Pour toute question : <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a></li>
          <li>Via les paramètres de votre navigateur (blocage ou suppression)</li>
          <li>
            Via le lien "
            <button
              type="button"
              onClick={reopenCookieBanner}
              className="text-[#F07B1D] underline font-semibold"
              data-testid="conf-manage-cookies-link"
            >
              Gérer mes cookies
            </button>
            " disponible en bas de chaque page
          </li>
        </ul>
        <p className="text-xs text-gray-600 mt-3">
          <strong>Navigateurs :</strong> Chrome → Paramètres → Confidentialité et sécurité → Cookies · Firefox → Options → Vie privée et sécurité → Cookies · Safari → Préférences → Confidentialité → Cookies · Edge → Paramètres → Cookies et autorisations de site.
        </p>
      </ConfSection>

      {/* 7 — Sécurité */}
      <ConfSection num="7" title="Sécurité des données">
        <p className="text-gray-700 text-sm mb-3">
          MGB School met en œuvre les mesures techniques et organisationnelles suivantes :
        </p>
        <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
          <li>Chiffrement <strong>SSL/TLS</strong> sur l'ensemble du site (HTTPS obligatoire)</li>
          <li>Paiements sécurisés via PayPal (certifié PCI-DSS niveau 1) — aucune donnée bancaire stockée sur nos serveurs</li>
          <li>Accès restreint aux données personnelles aux seules personnes habilitées</li>
          <li>Sauvegardes régulières des données hébergées</li>
          <li>Mots de passe hachés (bcrypt) et jamais stockés en clair</li>
          <li>Journalisation des accès aux données sensibles</li>
        </ul>
        <div className="mt-3 p-3 rounded-lg text-sm text-[#003087]" style={{ backgroundColor: "#E3F2FD" }}>
          En cas de violation de données susceptible d'engendrer un risque pour vos droits et libertés, nous nous engageons
          à notifier la CNIL <strong>dans les 72 heures</strong> et à vous informer sans délai injustifié (art. 33 et 34 RGPD).
        </div>
      </ConfSection>

      {/* 8 — Transferts hors UE */}
      <ConfSection num="8" title="Transferts hors Union européenne">
        <p className="text-gray-700 text-sm mb-2">
          Certains de nos sous-traitants (Anthropic, Meta) sont établis aux États-Unis. Ces transferts sont encadrés par :
        </p>
        <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
          <li>Clauses contractuelles types adoptées par la Commission européenne</li>
          <li>Politiques de protection des données conformes au RGPD</li>
          <li>Mécanismes de certification reconnus (successeur du Privacy Shield — DPF)</li>
        </ul>
      </ConfSection>

      {/* 9 — Mineurs */}
      <ConfSection num="9" title="Données des mineurs">
        <p className="text-gray-700 text-sm">
          Les formations MGB School sont destinées à un public adulte professionnel. MGB School ne collecte pas
          sciemment de données personnelles concernant des personnes de moins de 15 ans. Si vous estimez qu'un mineur
          nous a communiqué ses données, contactez-nous à{" "}
          <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline">contact@mgbschool.fr</a> pour
          procéder à leur suppression immédiate.
        </p>
      </ConfSection>

      {/* 10 — Modifications */}
      <ConfSection num="10" title="Modifications de la présente politique">
        <p className="text-gray-700 text-sm">
          MGB School se réserve le droit de modifier la présente politique à tout moment, notamment pour se conformer
          à l'évolution de la réglementation ou à de nouvelles pratiques de traitement. La date de la dernière mise à
          jour est indiquée en haut de ce document. Toute modification substantielle vous sera notifiée par e-mail si
          vous êtes client ou abonné à notre newsletter.
        </p>
      </ConfSection>

      {/* 11 — DPO */}
      <ConfSection num="11" title="Contact — Délégué à la Protection des Données">
        <p className="text-gray-700 text-sm mb-2">
          MGB School n'est pas dans l'obligation légale de désigner un DPO compte tenu de sa taille et de la nature de
          ses traitements. Le responsable du traitement, <strong>Maryse GALITA</strong>, assume directement cette fonction.
        </p>
        <div className="p-3 rounded-lg text-sm text-[#003087]" style={{ backgroundColor: "#E3F2FD" }}>
          Contact pour toute question RGPD :{" "}
          <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline font-semibold">contact@mgbschool.fr</a>
          {" "}— MGB School, 25 rue de Ponthieu, 75008 Paris.
        </div>
      </ConfSection>

      <div className="mt-8 pt-6 border-t border-gray-200 text-xs text-gray-500 text-center leading-relaxed">
        Document établi conformément au Règlement (UE) 2016/679 du Parlement européen et du Conseil du 27 avril 2016
        relatif à la protection des personnes physiques à l'égard du traitement des données à caractère personnel.
        <br />
        <strong>MGB School — SASU — SIRET 999 673 635 00010 — © 2026</strong>
      </div>
    </Card>
  </LegalPage>
);

// Helpers for the Confidentialite layout
const ConfSection = ({ num, title, children }) => (
  <section className="mt-8" data-testid={`conf-section-${num}`}>
    <h2
      className="font-heading text-xl md:text-2xl font-bold text-[#003087] mb-4 pb-2 inline-block relative"
    >
      {num}. {title}
      <span className="absolute left-0 bottom-0 w-16 h-[3px] bg-[#F07B1D] rounded-full"></span>
    </h2>
    <div>{children}</div>
  </section>
);

const ConfSubtitle = ({ children, className = "" }) => (
  <h3 className={`font-heading text-base font-semibold text-[#2196F3] mt-2 mb-2 ${className}`}>
    {children}
  </h3>
);

const ConfTable = ({ head, rows }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-[#003087] text-white">
          {head.map((h) => (
            <th key={h} className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wide">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={row.join("|")}
            style={{ backgroundColor: i % 2 === 0 ? "#FFFFFF" : "#E3F2FD" }}
          >
            {row.map((cell, ci) => (
              <td key={`${i}-${ci}`} className="px-3 py-2 text-gray-700 align-top">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ===============================
// MAIN APP
// ===============================
function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <Routes>
          {/* Admin Routes (without Header/Footer) */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/qualiopi" element={<AdminAdvancedPage />} />

          {/* Apprenant Portal (without Header/Footer) */}
          <Route path="/apprenant/login" element={<ApprenantLoginPage />} />
          <Route path="/apprenant" element={<ApprenantDashboardPage />} />
          <Route path="/apprenant/formation/:code" element={<ApprenantFormationPage />} />

          {/* Public Routes (with Header/Footer) */}
          <Route path="/*" element={<PublicLayout />} />
        </Routes>
        <CookieBanner />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

// Public Layout with Header and Footer
const PublicLayout = () => {
  return (
    <>
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/catalogue" element={<CataloguePage />} />
          <Route path="/formations/:slug" element={<FormationDetailPage />} />
          <Route path="/otm" element={<BacProOtmPage />} />
          <Route path="/agregation" element={<AgregationPage />} />
          <Route path="/certificat" element={<CertificatPage />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/paiement/confirme" element={<PaiementConfirmePage />} />
          <Route path="/paiement/:code" element={<CheckoutPage />} />
          <Route path="/paiement/dossier/:token" element={<DossierPaiementPage />} />
          <Route path="/sessions" element={<SessionsPage />} />
          <Route path="/signature/:token" element={<SignaturePage />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/enquete/:token" element={<EnquetePage />} />
        </Routes>
      </main>
      <Footer />
      <ChatWidget />
    </>
  );
};

export default App;
